import AppContainer from 'components/App/AppContainer';
import { initMsalInstance } from 'config/auth';
import { initOktaInstance } from 'config/oktaAuth';
import { AUTH, PORTAL_CONFIG } from 'constants/index';
import 'i18n';
import React, { lazy, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'redux/store';
import { AppService } from 'services';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import * as serviceWorker from './serviceWorker';
import { format } from 'i18n';
import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { isProxied, proxyPrefix } from 'utils/proxy';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { TeamsLoginEndPath, TeamsLoginStartPath } from 'utils/routes';
import queryString from 'query-string';
import { initOidcInstance } from 'config/oidcAuth';

const defaultPortalName = "UPtime - Portal";
const localeLocalFolder = `${window.location.origin}/portal-web-locales`;

const LoginEndPage = lazy(() => import('pages/TeamsAuthPages/LoginEndPage'));
const LoginStartPage = lazy(() => import('pages/TeamsAuthPages/LoginStartPage'));

const getLocaleURL = (config: any) => {
  const isLocal = (config.environment === 'local' || window.location.hostname === 'localhost') &&
    config.environment !== 'local-with-cdn';
  return isLocal ? localeLocalFolder : (config.localeURL ? config.localeURL : window.location.origin + "/cdn");
};

const DynamicTheme = () => {
  const [loading, setLoading] = useState(true);

  const baseName = isProxied() ? proxyPrefix + '/' : '/';

  useEffect(() => {
    (async () => {
      try {
        const config = await AppService.getEnvConfig();
        if (!config) return;

        document.title = config.portalName || defaultPortalName;

        i18n
          .use(Backend)
          .use(initReactI18next)
          .init({
            lng: localStorage.getItem('LANGUAGE') || 'en',
            fallbackLng: 'en',
            interpolation: {
              escapeValue: false,
              format,
            },
            ns: ['common', 'commonStatus', 'footer'],
            backend: {
              loadPath: `${getLocaleURL(config)}/locales/{{lng}}/{{ns}}.json`,
            },
          });

        sessionStorage.setItem(AUTH.CONFIGURATION, JSON.stringify(config));
        localStorage.setItem(AUTH.CONFIGURATION, JSON.stringify(config));
        sessionStorage.setItem(
          AUTH.AUTH_BY,
          config.authentication.provider.toUpperCase(),
        );

        const { hideTopNav } = queryString.parse(window.location.search);
        if (hideTopNav) {
          sessionStorage.setItem(PORTAL_CONFIG, JSON.stringify({ hideTopNav }));
        }

        switch (config.authentication.provider) {
          case 'oidc':
            initOidcInstance({
              authority: config.authentication.providers.oidc.authority,
              clientId: config.authentication.providers.oidc.clientId,
              redirectUri: config.authentication.providers.oidc.redirectUri,
              signoutUri: config.authentication.providers.oidc.signoutUri
            });
            break;
          case 'azure':
            initMsalInstance({
              authority: config.authentication.providers.azure.url,
              clientId: config.authentication.providers.azure.clientId,
            });
            break;
          default:
            initOktaInstance({
              issuer: config.authentication.providers.okta.url,
              clientId: config.authentication.providers.okta.clientId,
              logo: config.authentication.providers.okta.logo,
            });
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  if (loading) return <></>;

  return (
    <Provider store={store}>
      <BrowserRouter basename={baseName}>
        <Switch>
          <Route path={TeamsLoginStartPath} component={LoginStartPage} />
          <Route path={TeamsLoginEndPath} component={LoginEndPage} />
          <Route
            path="*"
            render={() => <AppContainer />}
          />
        </Switch>
      </BrowserRouter>
    </Provider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(<DynamicTheme />);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
